import React, { useState, useEffect } from 'react';
import { Flex, VStack, useToast, Image } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import apiEndpoint from '../config/data';
import {
  Button,
  PasswordInput,
  TextInput,
  BodyText,
  HeadingText,
  Link,
} from '@spotoninc/nexus-react';
import { LockIcon } from '@spotoninc/nexus-web-assets';
import EmailIcon from '../../media/images/EmailIcon.svg';
import {
  setUserId,
  fetchUserInfoFromToken,
} from '../../redux/slices/userSlice';
import {
  extractUserFromJWT,
  logJWTContents,
  isTokenExpired,
} from '../../utils/jwt';
import appVersion from '../../utils/appVersion';
import { useFeatureFlagsFromContext } from '../../launchdarkly';
import SSOLoginButton from '../auth/SSOLoginButton';

const LoginContent = ({ projectName = 'Profit Assist', onViewChange }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const toast = useToast();
  const dispatch = useDispatch();
  const flags = useFeatureFlagsFromContext();
  const showReskin = flags?.showReskin || false;
  const oktaSsoAuthEnabled = flags?.oktaSsoAuth || false;

  console.log(`App Version: ${appVersion}`);
  console.log('LoginContent - Feature flags:', {
    showReskin,
    oktaSsoAuthEnabled,
    allFlags: flags,
  });

  useEffect(() => {
    // Only check for existing authentication if oktaSsoAuth is enabled
    if (!oktaSsoAuthEnabled) {
      return;
    }

    // Check if user is already authenticated
    const accessToken = localStorage.getItem('access');

    if (accessToken && !isTokenExpired(accessToken)) {
      // Redirect to overview when authenticated
      navigate('/overview');
    }
  }, [navigate, oktaSsoAuthEnabled]);

  const handleLogin = async () => {
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email || !password) {
      toast({
        title: 'Error',
        description: 'Please enter both email and password',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!emailRegex.test(email)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    try {
      const response = await fetch(apiEndpoint + '/api/token/', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();

      if (data.access) {
        localStorage.setItem('access', data.access);
        localStorage.setItem('refresh', data.refresh);

        // For debugging - log the JWT contents
        logJWTContents(data.access);

        // Extract user ID from JWT token and store in Redux
        const userInfo = extractUserFromJWT(data.access);
        dispatch(setUserId(userInfo.userId));

        // Fetch complete user information using the new endpoint that works with both SSO and JWT
        dispatch(fetchUserInfoFromToken());

        // Navigate based on showReskin feature flag
        if (showReskin) {
          navigate('/overview');
        } else {
          navigate('/Dash');
        }
      } else {
        toast({
          title: 'Error',
          description: 'Please check your credentials',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: 'An error occurred. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <VStack w="100%" h="100%" spacing={8} position="relative">
      <Flex direction="column" align="center" gap={2}>
        <HeadingText variant="heading2" color="neutral.900">
          Welcome to {projectName}
        </HeadingText>
        <BodyText variant="body2" color="neutral.600">
          Don't have an account yet?
          <Link
            href="/register"
            variant="primary"
            style={{
              textDecoration: 'none',
            }}
          >
            Sign up now
          </Link>
        </BodyText>
      </Flex>

      <VStack spacing={4} w="auto" minW="70%">
        <TextInput
          type="email"
          placeholder="Email address"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={handleKeyPress}
          leadingIcon={() => <Image src={EmailIcon} alt="email" />}
          style={{
            width: '100%',
          }}
        />

        <PasswordInput
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={handleKeyPress}
          leadingIcon={() => <LockIcon />}
          style={{
            width: '100%',
          }}
        />

        <Button
          variant="primary"
          onClick={handleLogin}
          isLoading={loading}
          // loadingText="Logging in..."
          fullWidth
          className="centered-spinner-button"
        >
          Log in
        </Button>

        {/* SSO Login Button - only show if oktaSsoAuth feature flag is enabled */}
        {oktaSsoAuthEnabled && (
          <SSOLoginButton
            variant="outline"
            colorScheme="blue"
            size="md"
            width="full"
          />
        )}

        <Link
          onClick={() => onViewChange && onViewChange('forgotPassword')}
          style={{
            textDecoration: 'none',
            cursor: 'pointer',
          }}
        >
          Forgot password?
        </Link>
      </VStack>
    </VStack>
  );
};

export default LoginContent;
